import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { generatePath, useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CircularProgress, Paper, Typography } from '@material-ui/core'
import { Container } from '../../../components'
import { Routes } from '../../../navigation/config/routes'
import { useUser } from '../../../hooks'
import {
    useVendorSubPricingDetail,
    useVendorSubPricingSummary,
    useVendorSubPricingSaveLines,
} from '../../../hooks/useSubPricingMatrix'
import { SubsPricingSidebar } from './SubsPricingSidebar'
import { SubsPricingMatrixPanel } from './SubsPricingMatrixPanel'
import {
    findServiceBlock,
    formatSubVendorCoverageHint,
} from './subsPricingUtils'

interface Props {
    /** From route `/subs/pricing/:subUserId` — absent on `/subs/pricing`. */
    routeSubUserId?: string
}

export const SubsPricingTab = ({ routeSubUserId }: Props) => {
    const { workspaceUser } = useUser()
    const history = useHistory()
    const location = useLocation()

    const vendorCompanyId = workspaceUser?.active_workspace?.id ?? null

    const [bootstrapComplete, setBootstrapComplete] = useState(false)
    const [matrixDirty, setMatrixDirty] = useState(false)

    const [selectedSubUserId, setSelectedSubUserId] = useState<number | null>(
        null,
    )
    const [selectedApartmentId, setSelectedApartmentId] = useState<
        number | null
    >(null)
    const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
        null,
    )

    const summaryQuery = useVendorSubPricingSummary({
        vendor_id: vendorCompanyId,
        sub_vendor_user_id: selectedSubUserId,
        apartment_id: selectedApartmentId,
    })

    const detailQuery = useVendorSubPricingDetail({
        vendor_id: vendorCompanyId,
        sub_vendor_user_id: selectedSubUserId,
        apartment_id: selectedApartmentId,
    })

    const saveLinesMutation = useVendorSubPricingSaveLines()

    useEffect(() => {
        setBootstrapComplete(false)
        setSelectedSubUserId(null)
        setSelectedApartmentId(null)
        setSelectedServiceId(null)
        setMatrixDirty(false)
    }, [vendorCompanyId])

    /** First summary payload: optional URL sub only — workspace/service stay unset until the user picks. */
    useEffect(() => {
        const summary = summaryQuery.data
        if (
            bootstrapComplete ||
            !summary ||
            vendorCompanyId == null ||
            summary.vendor_id !== vendorCompanyId
        ) {
            return
        }

        const crewIds = new Set(summary.crew.map((c) => c.sub_vendor_user_id))
        const routeIdParsed = routeSubUserId
            ? parseInt(routeSubUserId, 10)
            : NaN
        const routeValid =
            Number.isFinite(routeIdParsed) && crewIds.has(routeIdParsed)

        if (
            routeSubUserId &&
            Number.isFinite(routeIdParsed) &&
            !routeValid &&
            crewIds.size > 0
        ) {
            toast.info(
                'That linked team member is not on your crew. Select a team member to continue.',
            )
        }

        if (summary.crew.length === 0) {
            setBootstrapComplete(true)
            return
        }

        if (routeValid) {
            setSelectedSubUserId(routeIdParsed)
        }

        setBootstrapComplete(true)
    }, [bootstrapComplete, routeSubUserId, summaryQuery.data, vendorCompanyId])

    /** Keep selection in sync if URL `:subUserId` changes after load. */
    useEffect(() => {
        const summary = summaryQuery.data
        if (
            !bootstrapComplete ||
            !summary ||
            !routeSubUserId ||
            vendorCompanyId == null ||
            summary.vendor_id !== vendorCompanyId
        ) {
            return
        }
        const routeIdParsed = parseInt(routeSubUserId, 10)
        if (!Number.isFinite(routeIdParsed)) return

        const crewIds = new Set(summary.crew.map((c) => c.sub_vendor_user_id))
        if (!crewIds.has(routeIdParsed)) return
        if (selectedSubUserId !== routeIdParsed) {
            setSelectedSubUserId(routeIdParsed)
        }
    }, [
        bootstrapComplete,
        routeSubUserId,
        selectedSubUserId,
        summaryQuery.data,
        vendorCompanyId,
    ])

    /** Clamp workspace/service if current sub excludes prior picks. */
    useEffect(() => {
        const summary = summaryQuery.data
        if (
            !summary ||
            !bootstrapComplete ||
            vendorCompanyId == null ||
            summary.vendor_id !== vendorCompanyId
        ) {
            return
        }

        const aptIds = new Set(summary.workspaces.map((w) => w.apartment_id))
        const svcIds = new Set(summary.services.map((s) => s.service_id))

        if (
            selectedApartmentId != null &&
            aptIds.size > 0 &&
            !aptIds.has(selectedApartmentId)
        ) {
            setSelectedApartmentId(null)
            setSelectedServiceId(null)
        }
        if (
            selectedServiceId != null &&
            svcIds.size > 0 &&
            !svcIds.has(selectedServiceId)
        ) {
            setSelectedServiceId(null)
        }
    }, [
        bootstrapComplete,
        selectedApartmentId,
        selectedServiceId,
        summaryQuery.data,
        vendorCompanyId,
    ])

    useEffect(() => {
        if (
            location.pathname !== Routes.vendor.subsPricingRoot ||
            selectedSubUserId == null ||
            !bootstrapComplete
        ) {
            return
        }
        history.replace(
            generatePath(Routes.vendor.subsPricing, {
                subUserId: String(selectedSubUserId),
            }),
        )
    }, [bootstrapComplete, history, location.pathname, selectedSubUserId])

    const serviceCoverageHints = useMemo(() => {
        const hints: Record<number, string> = {}
        const detail = detailQuery.data
        const services = summaryQuery.data?.services
        if (!detail || !services?.length) return hints

        for (const s of services) {
            hints[s.service_id] = formatSubVendorCoverageHint(
                findServiceBlock(detail, s.service_id),
            )
        }
        return hints
    }, [detailQuery.data, summaryQuery.data?.services])

    const selectSub = useCallback(
        (userId: number | null) => {
            if (
                matrixDirty &&
                !window.confirm('Discard unsaved sub-contract changes?')
            ) {
                return
            }
            if (userId == null) {
                setSelectedSubUserId(null)
                setSelectedApartmentId(null)
                setSelectedServiceId(null)
                history.replace(Routes.vendor.subsPricingRoot)
                return
            }
            setSelectedSubUserId(userId)
            setSelectedApartmentId(null)
            setSelectedServiceId(null)
            history.push(
                generatePath(Routes.vendor.subsPricing, {
                    subUserId: String(userId),
                }),
            )
        },
        [history, matrixDirty],
    )

    const selectWorkspace = useCallback(
        (apartmentId: number | null) => {
            if (
                matrixDirty &&
                !window.confirm('Discard unsaved sub-contract changes?')
            ) {
                return
            }
            setSelectedApartmentId(apartmentId)
            setSelectedServiceId(null)
        },
        [matrixDirty],
    )

    const selectService = useCallback(
        (serviceId: number) => {
            if (
                matrixDirty &&
                !window.confirm('Discard unsaved sub-contract changes?')
            ) {
                return
            }
            setSelectedServiceId(serviceId)
        },
        [matrixDirty],
    )

    const summary = summaryQuery.data
    const crewLabel = summary?.crew.find(
        (c) => c.sub_vendor_user_id === selectedSubUserId,
    )?.name

    const workspaceLabel = summary?.workspaces.find(
        (w) => w.apartment_id === selectedApartmentId,
    )?.name

    const selectedServiceName = summary?.services.find(
        (s) => s.service_id === selectedServiceId,
    )?.name

    const sliceComplete =
        selectedSubUserId != null &&
        selectedApartmentId != null &&
        selectedServiceId != null

    let pricingEmptyMessage = ''
    if (!sliceComplete) {
        if (selectedSubUserId == null) {
            pricingEmptyMessage =
                'Select a team member and workspace to load pricing. Then choose a service.'
        } else if (selectedApartmentId == null) {
            pricingEmptyMessage =
                'Select a workspace for this team member to load pricing rows. Then choose a service.'
        } else {
            pricingEmptyMessage =
                'Select a service to view and edit sub-contract rates.'
        }
    }

    if (vendorCompanyId == null) {
        return null
    }

    if (!bootstrapComplete && summaryQuery.isError) {
        return (
            <Container style={{ paddingTop: 16, alignSelf: 'stretch' }}>
                <Typography color="textSecondary">
                    Could not load sub-pricing overview. Retry from the Pricing
                    tab or refresh the page.
                </Typography>
            </Container>
        )
    }

    const summaryInitialLoading =
        !bootstrapComplete && summaryQuery.isLoading && !summaryQuery.data

    if (summaryInitialLoading) {
        return (
            <Container
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 48,
                    width: '100%',
                    alignSelf: 'stretch',
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    return (
        <Container
            direction="row"
            alignItems="flex-start"
            style={{
                flexWrap: 'nowrap',
                gap: 16,
                width: '100%',
                alignSelf: 'stretch',
            }}
        >
            <SubsPricingSidebar
                crew={summary?.crew ?? []}
                workspaces={summary?.workspaces ?? []}
                services={summary?.services ?? []}
                selectedSubUserId={selectedSubUserId}
                selectedApartmentId={selectedApartmentId}
                selectedServiceId={selectedServiceId}
                serviceCoverageHints={serviceCoverageHints}
                onSelectSub={selectSub}
                onSelectWorkspace={selectWorkspace}
                onSelectService={selectService}
            />
            {sliceComplete ? (
                <SubsPricingMatrixPanel
                    matrix={detailQuery.data ?? null}
                    matrixLoading={detailQuery.isLoading}
                    detailDataUpdatedAt={
                        typeof detailQuery.dataUpdatedAt === 'number'
                            ? detailQuery.dataUpdatedAt
                            : 0
                    }
                    vendorId={vendorCompanyId}
                    subVendorUserId={selectedSubUserId}
                    apartmentId={selectedApartmentId}
                    onSave={(body) => saveLinesMutation.mutateAsync(body)}
                    isSaving={saveLinesMutation.isLoading}
                    onDirtyChange={setMatrixDirty}
                    selectedServiceId={selectedServiceId}
                    selectedServiceName={selectedServiceName}
                    workspaceLabel={workspaceLabel}
                    crewLabel={crewLabel}
                />
            ) : (
                <Paper
                    variant="outlined"
                    style={{
                        flex: 1,
                        minWidth: 0,
                        padding: 24,
                        alignSelf: 'stretch',
                    }}
                >
                    <Typography variant="h6" style={{ marginBottom: 8 }}>
                        Pricing matrix
                    </Typography>
                    <Typography color="textSecondary" variant="body1">
                        {pricingEmptyMessage}
                    </Typography>
                </Paper>
            )}
        </Container>
    )
}
